// Mobile version control: a calm T3-dark pill in the composer-home top row (beside undo/redo)
// that opens a bottom-sheet Versions timeline. Same journey + preview/restore as desktop, styled
// for the dark mobile chrome. Renders nothing until the post has a version journey.

import { Check, History, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import { MobileBottomSheet } from '../../components/MobileBottomSheet'
import { scoreTier } from '../../process/creative-process.types'
import { tierColor } from '../../components/process/tier-color'
import type { CreativeVersionSummary } from '../../store/versions-store'
import { useAttachmentUrl } from '../use-attachment-url'
import { useComposerEditor } from './CompositionEditorProvider'
import { kindIcon, relativeTime } from './version-format'
import { useCreativeVersions } from './use-versions'

function MobileVersionRow({
  version,
  prevScore,
  isCurrent,
  isTip,
  onPreview,
  onRestore,
}: {
  version: CreativeVersionSummary
  prevScore?: number | null
  isCurrent: boolean
  isTip: boolean
  onPreview: () => void
  onRestore: () => void
}) {
  const thumb = useAttachmentUrl(version.masterAttachmentId ?? undefined)
  const score = version.score ?? null
  const color = score != null ? tierColor(scoreTier(score)) : 'var(--paper-faint)'
  const delta = score != null && prevScore != null && prevScore !== score ? score - prevScore : null

  return (
    <div
      className={`flex items-center gap-3 border-b border-line/60 px-4 py-3 ${isCurrent ? 'bg-bg-3/50' : ''}`}
    >
      <button
        type="button"
        onClick={onPreview}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-md border border-line bg-bg-3">
          {thumb ? (
            <img src={thumb} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="flex h-full w-full items-center justify-center text-paper-faint">
              {kindIcon(version.kind)}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="flex items-center gap-1 text-[14px] font-medium text-paper">
              {kindIcon(version.kind)}
              {version.label ?? `v${version.seq}`}
            </span>
            {isTip && (
              <span className="rounded bg-bg-3 px-1 text-[9px] font-medium tracking-wide text-paper-faint uppercase">
                Latest
              </span>
            )}
          </div>
          {version.feedback && (
            <p className="truncate text-[12px] text-paper-faint">{version.feedback}</p>
          )}
          <span className="text-[11px] text-paper-faint tabular-nums">
            {delta != null ? `${delta > 0 ? '+' : ''}${delta} · ` : ''}
            {relativeTime(version.createdAt)}
          </span>
        </div>
        <span
          className="flex flex-shrink-0 items-center gap-1 text-[14px] font-semibold tabular-nums"
          style={{ color }}
        >
          <span className="h-2 w-2 rounded-full" style={{ background: color }} />
          {score ?? '—'}
        </span>
      </button>
      <div className="w-7 flex-shrink-0">
        {isCurrent ? (
          <Check className="h-4 w-4 text-paper" />
        ) : (
          <button
            type="button"
            onClick={onRestore}
            aria-label={`Restore ${version.label ?? `v${version.seq}`}`}
            className="flex h-9 w-9 items-center justify-center rounded-full text-paper-dim active:bg-bg-3"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}

export function MobileVersionControl() {
  const ctx = useComposerEditor()
  const creativeId = ctx?.creativeId
  const versions = useCreativeVersions(creativeId)
  const [open, setOpen] = useState(false)

  if (!ctx || versions.length === 0) return null
  const { previewSeq, previewVersion, exitPreview, restoreVersion } = ctx

  const total = versions.length
  const tipSeq = versions[total - 1]!.seq
  const currentSeq = previewSeq ?? tipSeq
  const currentIdx = versions.findIndex(v => v.seq === currentSeq)
  const rows = [...versions].reverse()

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Version history"
        className="ml-auto flex h-9 items-center gap-1.5 rounded-full border border-line px-3 text-[13px] text-paper-dim active:bg-bg-3"
      >
        <History size={15} className="text-paper-faint" />
        <span className="text-paper-faint tabular-nums">
          v{currentIdx + 1}/{total}
        </span>
      </button>

      <MobileBottomSheet open={open} onClose={() => setOpen(false)} title="Versions" height="half">
        {previewSeq != null && (
          <div className="flex items-center justify-between border-b border-line/60 px-4 py-2">
            <span className="text-[13px] text-paper-dim">Previewing v{currentIdx + 1}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  exitPreview()
                }}
                className="rounded-full border border-line px-3 py-1 text-[12px] text-paper-dim"
              >
                Latest
              </button>
              <button
                type="button"
                onClick={() => {
                  void restoreVersion(currentSeq)
                  setOpen(false)
                }}
                className="text-ink-on-acid flex items-center gap-1 rounded-full bg-acid px-3 py-1 text-[12px] font-medium"
              >
                <RotateCcw className="h-3 w-3" />
                Restore
              </button>
            </div>
          </div>
        )}
        <div className="overflow-y-auto">
          {rows.map(v => {
            const idx = versions.findIndex(x => x.seq === v.seq)
            const prev = idx > 0 ? versions[idx - 1]!.score : null
            return (
              <MobileVersionRow
                key={v.seq}
                version={v}
                prevScore={prev}
                isCurrent={v.seq === currentSeq}
                isTip={v.seq === tipSeq}
                onPreview={() => {
                  if (v.seq === tipSeq) exitPreview()
                  else void previewVersion(v.seq)
                }}
                onRestore={() => {
                  void restoreVersion(v.seq)
                  setOpen(false)
                }}
              />
            )
          })}
        </div>
      </MobileBottomSheet>
    </>
  )
}
